import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { 
  Brain, 
  Building2, 
  MapPin, 
  Calendar,
  Euro,
  ExternalLink,
  Heart,
  Loader2,
  Sparkles,
  AlertCircle,
  ChevronRight,
  RefreshCw,
  FileText,
  Clock,
  Zap
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import DCEOrderModal from "../components/DCEOrderModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState([]);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [showDCEModal, setShowDCEModal] = useState(false);
  const [selectedTenderForDCE, setSelectedTenderForDCE] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);

  const openDCEModal = (tender) => {
    setSelectedTenderForDCE(tender);
    setShowDCEModal(true);
  };

  useEffect(() => {
    fetchData();
    fetchTrialStatus();
  }, []);

  const fetchTrialStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/auth/trial-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrialStatus(response.data);
    } catch (error) {
      console.error("Error fetching trial status:", error);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch company profile
      let profile = null;
      try {
        const profileResponse = await axios.get(`${API_URL}/api/company`, { headers });
        profile = profileResponse.data;
        setCompanyProfile(profile);
      } catch (profileError) {
        if (profileError.response?.status !== 400) {
          console.error("Profile error:", profileError);
        }
      }

      // Get recommendations if we have a profile with sector
      const profileSector = profile?.sector || profile?.activity_sector;
      if (profileSector) {
        // Extract region name - handle both 'region' and 'regions' array
        let regionName = profile.region || (profile.regions && profile.regions.length > 0 ? profile.regions[0] : "") || "France entière";
        if (regionName.includes(",")) {
          regionName = regionName.split(",").pop().trim();
        }
        
        // Define default keywords for each sector (these are actual search terms)
        const sectorDefaultKeywords = {
          "Travaux": ["travaux", "construction", "rénovation", "bâtiment"],
          "travaux": ["travaux", "construction", "rénovation", "bâtiment"],
          "BTP": ["travaux", "construction", "rénovation", "bâtiment"],  // Alias for backward compatibility
          "btp": ["travaux", "construction", "rénovation", "bâtiment"],  // Alias for backward compatibility
          "Services": ["prestations", "services", "conseil", "études"],
          "services": ["prestations", "services", "conseil", "études"],
          "Fournitures": ["fournitures", "équipements", "matériels"],
          "fournitures": ["fournitures", "équipements", "matériels"],
          "Informatique": ["informatique", "numérique", "logiciel"],
          "informatique": ["informatique", "numérique", "logiciel"],
          "Nettoyage": ["nettoyage", "propreté", "entretien"],
          "nettoyage": ["nettoyage", "propreté", "entretien"],
          "Sécurité": ["sécurité", "surveillance", "gardiennage"],
          "sécurité": ["sécurité", "surveillance", "gardiennage"],
          "Formation": ["formation", "enseignement"],
          "formation": ["formation", "enseignement"]
        };
        
        // Prepare keywords - use profile keywords or sector defaults
        const keywords = profile.keywords && profile.keywords.length > 0 
          ? profile.keywords 
          : sectorDefaultKeywords[profileSector] || [profileSector.toLowerCase()];

        // Use FAST endpoint first (no AI, instant results)
        try {
          const fastResponse = await axios.post(`${API_URL}/api/fast-recommendations`, {
            sector: profileSector,
            region: regionName,
            keywords: keywords
          });
          if (fastResponse.data && fastResponse.data.length > 0) {
            setRecommendations(fastResponse.data);
          }
        } catch (fastError) {
          console.error("Fast recommendations failed:", fastError);
          // Fallback to trial recommendations
          try {
            const trialResponse = await axios.post(`${API_URL}/api/trial/recommendations`, {
              sector: profileSector,
              region: regionName,
              keywords: keywords
            });
            if (trialResponse.data && trialResponse.data.length > 0) {
              setRecommendations(trialResponse.data);
            }
          } catch (trialError) {
            console.error("All recommendations failed");
          }
        }

        // Fetch favorites silently
        try {
          const favsResponse = await axios.get(`${API_URL}/api/favorites`, { headers });
          setFavorites(favsResponse.data.map(f => f.id));
        } catch {}
      }
    } catch (error) {
      console.error("Unexpected error:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (tenderId) => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      if (favorites.includes(tenderId)) {
        await axios.delete(`${API_URL}/api/favorites/${tenderId}`, { headers });
        setFavorites(prev => prev.filter(id => id !== tenderId));
        toast.success("Retiré des favoris");
      } else {
        await axios.post(`${API_URL}/api/favorites/${tenderId}`, {}, { headers });
        setFavorites(prev => [...prev, tenderId]);
        toast.success("Ajouté aux favoris");
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour des favoris");
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600 bg-green-100";
    if (score >= 60) return "text-yellow-600 bg-yellow-100";
    return "text-orange-600 bg-orange-100";
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-violet-600 mx-auto mb-4" />
            <p className="text-slate-600">Chargement des marchés...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // No company profile - prompt to create one
  if (!companyProfile) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-center py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-20 h-20 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-6">
              <Building2 className="w-10 h-10 text-violet-600" />
            </div>
            <h2 className="font-display text-2xl font-bold text-slate-900 mb-4">
              Créez votre profil entreprise
            </h2>
            <p className="text-slate-600 mb-8">
              Pour recevoir des recommandations personnalisées, commencez par renseigner les informations de votre entreprise.
            </p>
            <Button
              onClick={() => navigate("/profile")}
              className="bg-violet-700 hover:bg-violet-800 text-white rounded-full px-8 py-6 text-lg"
              data-testid="create-profile-btn"
            >
              <Building2 className="w-5 h-5 mr-2" />
              Créer mon profil
            </Button>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Trial Status Banner */}
        {trialStatus && !trialStatus.has_paid_subscription && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {trialStatus.trial_active ? (
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-4 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold">Essai gratuit : {trialStatus.trial_days_remaining} jours restants</p>
                      <p className="text-sm text-amber-100">Passez à la version Pro pour un accès illimité</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate("/pricing")}
                    className="bg-white text-orange-600 hover:bg-orange-50"
                    data-testid="upgrade-trial-btn"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Passer à Pro
                  </Button>
                </div>
              </div>
            ) : trialStatus.trial_expired && (
              <div className="bg-gradient-to-r from-red-500 to-rose-500 rounded-xl p-4 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                      <AlertCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold">Votre essai gratuit a expiré</p>
                      <p className="text-sm text-red-100">Abonnez-vous pour continuer à utiliser toutes les fonctionnalités</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate("/pricing")}
                    className="bg-white text-red-600 hover:bg-red-50"
                    data-testid="subscribe-expired-btn"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    S'abonner maintenant
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-start justify-between">
            <div>
              <h1 className="font-display text-3xl font-bold text-slate-900">
                Bonjour, {user?.full_name?.split(' ')[0]} !
              </h1>
              <p className="text-slate-600 mt-1">
                Voici vos 3 marchés publics recommandés par notre IA
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate("/profile")}
              className="border-slate-200"
              data-testid="edit-profile-btn"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Modifier le profil
            </Button>
          </div>
          
          {/* Quick Actions */}
          <div className="flex gap-3 mt-4">
            <Button
              onClick={() => navigate("/synthese-marches")}
              className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white"
              data-testid="synthese-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Analyser un document (IA)
            </Button>
          </div>
        </motion.div>

        {/* Company Profile Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-gradient-to-r from-violet-600 to-indigo-600 border-0 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                    <Building2 className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold">{companyProfile.company_name}</h3>
                    <div className="flex items-center gap-4 mt-1 text-white/80 text-sm">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {companyProfile.location}, {companyProfile.region}
                      </span>
                      <span>{companyProfile.sector}</span>
                    </div>
                  </div>
                </div>
                <Badge className="bg-white/20 text-white hover:bg-white/30">
                  {user?.subscription_tier === "free" ? "Gratuit" : user?.subscription_tier === "pro" ? "Pro" : "Premium"}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* AI Recommendations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold text-slate-900">Recommandations IA</h2>
                <p className="text-sm text-slate-500">Basées sur votre profil entreprise</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                setLoading(true);
                fetchData();
              }}
              disabled={loading}
              className="text-violet-600 border-violet-200 hover:bg-violet-50"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              <span className="ml-2">{loading ? "Chargement..." : "Actualiser"}</span>
            </Button>
          </div>

          {recommendations.length === 0 ? (
            <Card className="border-dashed border-2 border-slate-200">
              <CardContent className="p-8 text-center">
                <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="font-medium text-slate-900 mb-2">
                  {loading 
                    ? "Chargement des recommandations..." 
                    : companyProfile 
                      ? "Aucun marché correspondant trouvé"
                      : "Aucune recommandation disponible"
                  }
                </h3>
                <p className="text-slate-600">
                  {loading 
                    ? "Nous analysons les marchés correspondant à votre profil..."
                    : companyProfile 
                      ? "Cliquez sur Actualiser pour relancer la recherche, ou modifiez vos mots-clés dans votre profil."
                      : "Complétez votre profil entreprise pour recevoir des recommandations personnalisées."
                  }
                </p>
                {companyProfile && !loading && (
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => {
                      setLoading(true);
                      fetchData();
                    }}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualiser les recommandations
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {recommendations.map((rec, index) => (
                <motion.div
                  key={rec.tender.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  <Card 
                    className="border-l-4 border-l-violet-500 hover:shadow-lg transition-all cursor-pointer group"
                    data-testid={`tender-card-${rec.tender.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <Badge className={`${getScoreColor(rec.match_score)} font-semibold`}>
                              <Sparkles className="w-3 h-3 mr-1" />
                              {rec.match_score}% compatible
                            </Badge>
                            <Badge variant="outline" className="text-slate-600">
                              {rec.tender.sector}
                            </Badge>
                            <Badge variant="outline" className={`text-xs ${
                              rec.tender.source === "BOAMP" ? "bg-blue-50 text-blue-700 border-blue-200" : 
                              rec.tender.source === "DECP" ? "bg-green-50 text-green-700 border-green-200" :
                              rec.tender.source === "TED" ? "bg-yellow-50 text-yellow-700 border-yellow-200" :
                              rec.tender.source === "EMARCHESPUBLICS" ? "bg-cyan-50 text-cyan-700 border-cyan-200" :
                              rec.tender.source === "SNCF" ? "bg-red-50 text-red-700 border-red-200" :
                              rec.tender.source === "ADP" ? "bg-purple-50 text-purple-700 border-purple-200" :
                              rec.tender.source === "RATP" ? "bg-teal-50 text-teal-700 border-teal-200" :
                              rec.tender.source === "MSEC" ? "bg-orange-50 text-orange-700 border-orange-200" :
                              rec.tender.source === "DEMATIS" ? "bg-pink-50 text-pink-700 border-pink-200" :
                              "bg-slate-50 text-slate-600"
                            }`}>
                              {rec.tender.source === "TED" ? "TED Europa" : 
                               rec.tender.source === "EMARCHESPUBLICS" ? "e-marchés" : 
                               rec.tender.source}
                            </Badge>
                          </div>
                          
                          <Link 
                            to={`/tender/${rec.tender.id}`}
                            state={{ match_reasons: rec.match_reasons, match_score: rec.match_score }}
                            className="block group-hover:text-violet-700 transition-colors"
                          >
                            <h3 className="font-display text-lg font-semibold text-slate-900 mb-2">
                              {rec.tender.title}
                            </h3>
                          </Link>
                          
                          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-4">
                            <span className="flex items-center gap-1">
                              <Building2 className="w-4 h-4" />
                              {rec.tender.buyer}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {rec.tender.region}
                            </span>
                            <span className="flex items-center gap-1">
                              <Euro className="w-4 h-4" />
                              {rec.tender.estimated_value}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              Date limite: {new Date(rec.tender.deadline).toLocaleDateString('fr-FR')}
                            </span>
                          </div>

                          {/* Match Reasons */}
                          <div className="bg-slate-50 rounded-lg p-3">
                            <p className="text-xs font-medium text-slate-500 mb-2">Pourquoi ce marché ?</p>
                            <ul className="space-y-1">
                              {rec.match_reasons.map((reason, i) => (
                                <li key={`item-${i}`} className="flex items-center gap-2 text-sm text-slate-700">
                                  <ChevronRight className="w-3 h-3 text-violet-500" />
                                  {reason}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        <div className="flex flex-col items-center gap-2">
                          <Progress 
                            value={rec.match_score} 
                            className="w-16 h-16 [&>div]:bg-violet-600"
                            style={{ transform: 'rotate(-90deg)' }}
                          />
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(rec.tender.id);
                              }}
                              className={favorites.includes(rec.tender.id) ? "text-red-500" : "text-slate-400"}
                              data-testid={`favorite-btn-${rec.tender.id}`}
                            >
                              <Heart className={`w-5 h-5 ${favorites.includes(rec.tender.id) ? "fill-current" : ""}`} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/tender/${rec.tender.id}`, { 
                                state: { match_reasons: rec.match_reasons, match_score: rec.match_score }
                              })}
                              className="text-slate-400 hover:text-violet-600"
                              data-testid={`view-btn-${rec.tender.id}`}
                            >
                              <ExternalLink className="w-5 h-5" />
                            </Button>
                          </div>
                          {/* DCE Button */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDCEModal(rec.tender);
                            }}
                            className="mt-2 text-xs border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                            data-testid={`dce-btn-${rec.tender.id}`}
                          >
                            <FileText className="w-3 h-3 mr-1" />
                            DCE 24h
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Upgrade CTA */}
        {user?.subscription_tier === "free" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Card className="bg-gradient-to-r from-indigo-500 to-violet-500 border-0 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-display text-lg font-semibold mb-1">
                      Passez à Pro pour plus de recommandations
                    </h3>
                    <p className="text-white/80 text-sm">
                      10 recommandations par semaine + alertes email + analyse approfondie
                    </p>
                  </div>
                  <Button 
                    onClick={() => navigate("/pricing")}
                    className="bg-white text-violet-700 hover:bg-slate-100"
                    data-testid="upgrade-btn"
                  >
                    Voir les offres
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>

      {/* DCE Order Modal */}
      <DCEOrderModal 
        isOpen={showDCEModal} 
        onClose={() => setShowDCEModal(false)} 
        tender={selectedTenderForDCE}
      />
    </DashboardLayout>
  );
};

export default DashboardPage;
